import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "@tellescope/forms";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import { Link } from "../../../../../components";
import {
  Badge,
  Button,
  Combobox,
  ComboboxOption,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { InboxZendeskRecipientItem } from "./InboxZendeskRecipientItem";
import { formatName } from "@tellescope/utils";
import { InboxZendeskCompositionFooter } from "./InboxZendeskCompositionFooter";
import { PAGE_HEADER_SIZE_STRING } from "../../../../../constants/header-sizing";

interface ZendeskBrand {
  id: number;
  name: string;
}

interface CcRecipient {
  id: string;
  email: string;
  name: string;
  type: "enduser" | "agent";
}

export const InboxZendeskCompositionPanel = () => {
  const { t } = useTranslation("inbox");
  const [enduserSearch, setEnduserSearch] = useState("");
  const [selectedEnduser, setSelectedEnduser] = useState<TellescopeModelFromName<"endusers">>();
  const [ccSearch, setCcSearch] = useState("");
  const [ccRecipients, setCcRecipients] = useState<CcRecipient[]>([]);
  const [isSending, setIsSending] = useState(false);

  // Search endusers for To field
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: enduserSearch ? { query: enduserSearch } : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  // Fetch Zendesk brands via proxy_read
  const brandsQuery = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Zendesk",
      type: "brands",
    },
    select: (data) => (data?.data as { brands: ZendeskBrand[] })?.brands ?? [],
    staleTime: 5 * 60 * 1000,
  });

  // Search endusers for CC
  const ccEndusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: ccSearch ? { query: ccSearch } : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  // Search users (agents) for CC
  const ccUsersQuery = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {
      limit: 20,
      search: ccSearch ? { query: ccSearch } : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  // Combine endusers and users into CC options
  const ccOptions = useMemo(() => {
    const selectedIds = new Set(ccRecipients.map((r) => r.id));

    const enduserOptions: ComboboxOption<string>[] = (ccEndusersQuery.data ?? [])
      .filter((enduser) => !selectedIds.has(enduser.id) && enduser.email)
      .map((enduser) => ({
        value: enduser.id,
        label: formatName(enduser),
        metadata: {
          id: enduser.id,
          type: "enduser" as const,
        },
      }));

    const userOptions: ComboboxOption<string>[] = (ccUsersQuery.data ?? [])
      .filter((user) => !selectedIds.has(user.id) && user.email)
      .map((user) => ({
        value: user.id,
        label: formatName(user),
        metadata: {
          id: user.id,
          type: "agent" as const,
        },
      }));

    return [...enduserOptions, ...userOptions];
  }, [ccEndusersQuery.data, ccUsersQuery.data, ccRecipients]);

  const form = useForm({
    fields: {
      enduserId: {
        type: "text",
        initialValue: "",
        required: true,
      },
      subject: {
        type: "text",
        initialValue: "",
        required: true,
      },
      brandId: {
        type: "text",
        initialValue: brandsQuery.data?.length === 1 ? String(brandsQuery.data[0]!.id) : "",
        required: false,
      },
      publicity: {
        type: "select",
        initialValue: "public",
      },
    },
    validateOn: "blur",
  });

  const handleRemoveCcRecipient = useCallback((recipientId: string) => {
    setCcRecipients((prev) => prev.filter((r) => r.id !== recipientId));
  }, []);

  const resolvedBrandId = useMemo(() => {
    const value = form.values.brandId;
    if (!value) return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [form.values.brandId]);

  const emailCCs = useMemo(
    () =>
      ccRecipients.map((r) => ({
        email: r.email,
        name: r.name,
      })),
    [ccRecipients]
  );

  // Find enduser or user by ID for CC selection
  const findEnduser = useCallback(
    (id: string): TellescopeModelFromName<"endusers"> | undefined => {
      return ccEndusersQuery.data?.find((e) => e.id === id);
    },
    [ccEndusersQuery.data]
  );

  const findUser = useCallback(
    (id: string): TellescopeModelFromName<"users"> | undefined => {
      return ccUsersQuery.data?.find((u) => u.id === id);
    },
    [ccUsersQuery.data]
  );

  return (
    <div className="relative flex flex-col h-full">
      {/* Loading Overlay */}
      {isSending && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="flex flex-col items-center gap-1">
              <p className="text-muted-foreground">{t("Sending...")}</p>
            </div>
          </div>
        </div>
      )}

      <div className={`flex items-center justify-between px-4 py-2 border-b h-[${PAGE_HEADER_SIZE_STRING}]`}>
        <h3 className="text-lg font-semibold">{t("New Zendesk Ticket")}</h3>
        <Link to="..">
          <Button variant="ghost" size="icon">
            <X />
          </Button>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="h-full space-y-4 flex flex-col justify-between">
          <div className="flex flex-col gap-4 w-full p-4">
            {/* To - Enduser Selector */}
            <div className="space-y-2">
              <Label htmlFor="recipient">{t("Enduser")}</Label>
              <Combobox
                value={{
                  value: form.getFieldProps("enduserId").value,
                  label: selectedEnduser ? formatName(selectedEnduser) : "",
                  metadata: { enduserId: form.getFieldProps("enduserId").value },
                }}
                formatSelectedValue={({ label }) => <p>{label}</p>}
                onChange={(newVal) => {
                  if (typeof newVal?.metadata?.enduserId === "string") {
                    const enduser = endusersQuery.data?.find((user) => user.id === newVal.metadata?.enduserId);
                    form.setFieldValue("enduserId", newVal?.metadata?.enduserId);
                    setSelectedEnduser(enduser);
                  } else {
                    form.setFieldValue("enduserId", "");
                    setSelectedEnduser(undefined);
                  }
                }}
                formatOption={(props) => (
                  <InboxZendeskRecipientItem
                    {...props}
                    enduser={endusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
                    recipientType="enduser"
                  />
                )}
                searchQuery={enduserSearch}
                setSearchQuery={setEnduserSearch}
                customFilter={(_, options) => options}
                placeholder={t("Select enduser...")}
                options={
                  endusersQuery.data?.map<ComboboxOption<string>>((enduser) => ({
                    value: enduser.id,
                    label: formatName(enduser),
                    metadata: {
                      enduserId: enduser.id,
                    },
                  })) ?? []
                }
              />
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">{t("Subject")}</Label>
              <Input
                id="subject"
                type="text"
                placeholder={t("Enter subject")}
                {...form.getFieldProps("subject")}
                onChange={(e) => {
                  form.setFieldValue("subject", e.target.value);
                }}
                className="h-9"
              />
            </div>

            {/* CC Recipients - searchable users and endusers */}
            <div className="space-y-2">
              <Label htmlFor="cc">{t("CC Recipients")}</Label>
              <Combobox
                value={undefined}
                formatSelectedValue={() => null}
                onChange={(newVal) => {
                  if (!newVal?.metadata?.id || !newVal?.metadata?.type) return;

                  const id = newVal.metadata.id as string;
                  const type = newVal.metadata.type as "enduser" | "agent";

                  // Prevent duplicates
                  if (ccRecipients.some((r) => r.id === id)) return;

                  if (type === "enduser") {
                    const enduser = findEnduser(id);
                    if (enduser?.email) {
                      setCcRecipients((prev) => [
                        ...prev,
                        {
                          id: enduser.id,
                          email: enduser.email!,
                          name: formatName(enduser),
                          type: "enduser",
                        },
                      ]);
                    }
                  } else {
                    const user = findUser(id);
                    if (user?.email) {
                      setCcRecipients((prev) => [
                        ...prev,
                        {
                          id: user.id,
                          email: user.email,
                          name: formatName(user),
                          type: "agent",
                        },
                      ]);
                    }
                  }
                }}
                formatOption={(props) => {
                  const type = props.option.metadata?.type as "enduser" | "agent" | undefined;
                  return (
                    <InboxZendeskRecipientItem
                      {...props}
                      enduser={type === "enduser" ? findEnduser(props.option.metadata?.id as string) : undefined}
                      user={type === "agent" ? findUser(props.option.metadata?.id as string) : undefined}
                      recipientType={type}
                    />
                  );
                }}
                searchQuery={ccSearch}
                setSearchQuery={setCcSearch}
                customFilter={(_, options) => options}
                placeholder={t("Search users and endusers...")}
                options={ccOptions}
              />

              {/* CC Recipients Chips */}
              {ccRecipients.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ccRecipients.map((recipient) => (
                    <Badge key={recipient.id} variant="secondary" className="gap-1">
                      <span>{recipient.name}</span>
                      <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">
                        {recipient.type === "agent" ? t("Agent") : t("Enduser")}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => handleRemoveCcRecipient(recipient.id)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Brand Selector */}
            <div className="space-y-2">
              <Label htmlFor="brand">{t("Brand")}</Label>
              <Select
                value={form.values.brandId}
                onValueChange={(value) => {
                  form.setFieldValue("brandId", value);
                }}
              >
                <SelectTrigger id="brand" className="h-9 w-full">
                  <SelectValue placeholder={t("Select brand...")} />
                </SelectTrigger>
                <SelectContent>
                  {brandsQuery.data?.map((brand) => (
                    <SelectItem key={brand.id} value={String(brand.id)}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="publicity">{t("Publicity")}</Label>
              <Select
                value={form.values.publicity}
                onValueChange={(value) => {
                  form.setFieldValue("publicity", value);
                }}
              >
                <SelectTrigger id="brand" className="h-9 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">{t("Public")}</SelectItem>
                  <SelectItem value="internal">{t("Internal Note")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <InboxZendeskCompositionFooter
            enduserId={form.values.enduserId || undefined}
            subject={form.values.subject}
            brandId={resolvedBrandId}
            ccRecipients={emailCCs.length > 0 ? emailCCs : undefined}
            onSend={() => {
              setIsSending(true);
              form.resetForm();
              setCcRecipients([]);
            }}
            onPollExhausted={() => setIsSending(false)}
          />
        </div>
      </div>
    </div>
  );
};
